import { Type } from 'main.core';
import { Analytics } from './api/analytics';

const GROUP_A = 'a';
const GROUP_B = 'b';
const STORAGE_KEY = 'b24network_abtest_groups';

export class Abtest
{
	constructor()
	{
		this.sessionId = '';
		this.experiments = new Map();
		this.groupCache = new Map();
		this.savedGroups = this.loadSavedGroups();
	}

	setSessionId(sessionId: string): void
	{
		this.sessionId = Type.isString(sessionId) ? sessionId : '';
	}

	setExperiments(experiments: Array<Object>): void
	{
		this.experiments.clear();

		if (!Type.isArray(experiments))
		{
			return;
		}

		experiments.forEach((experimentConfig) => {
			if (!Type.isPlainObject(experimentConfig))
			{
				return;
			}

			const code = String(experimentConfig.code ?? '');
			if (code === '')
			{
				return;
			}

			this.experiments.set(code, experimentConfig);
		});
	}

	resolveGroup(experimentConfig: Object): string
	{
		const code = String(experimentConfig?.code ?? '');
		if (code === '')
		{
			return GROUP_B;
		}

		if (this.groupCache.has(code))
		{
			return this.groupCache.get(code);
		}

		const savedGroup = this.savedGroups[code];
		if (savedGroup === GROUP_A || savedGroup === GROUP_B)
		{
			this.groupCache.set(code, savedGroup);

			return savedGroup;
		}

		const isSuitableHit = experimentConfig.isSuitableHit === true;
		if (!isSuitableHit)
		{
			this.groupCache.set(code, GROUP_B);

			return GROUP_B;
		}

		const percentForGroupA = this.normalizePercent(experimentConfig.percentForGroupA);
		const randomValue = this.getRandomPercentValue(code);
		const group = randomValue <= percentForGroupA ? GROUP_A : GROUP_B;

		this.groupCache.set(code, group);
		this.savedGroups[code] = group;
		this.saveGroupsToStorage();
		Analytics.emitSplitEvent(code, group);

		return group;
	}

	isGroupA(experimentConfig: Object): boolean
	{
		return this.resolveGroup(experimentConfig) === GROUP_A;
	}

	getAnalyticsLabels(): Object
	{
		const labels = {};

		Object.entries(this.savedGroups).forEach(([code, group]) => {
			if (group === GROUP_A || group === GROUP_B)
			{
				labels[`ab_${code}`] = group;
			}
		});

		return labels;
	}

	normalizePercent(value: any): number
	{
		const parsed = Number(value);
		if (!Number.isFinite(parsed))
		{
			return 0;
		}

		if (parsed < 0)
		{
			return 0;
		}

		if (parsed > 100)
		{
			return 100;
		}

		return Math.floor(parsed);
	}

	getRandomPercentValue(experimentCode: string): number
	{
		if (this.sessionId !== '')
		{
			const hash = this.hashCode(`${this.sessionId}:${experimentCode}`);

			return (Math.abs(hash) % 100) + 1;
		}

		return Math.floor(Math.random() * 100) + 1;
	}

	hashCode(value: string): number
	{
		let hash = 0;

		for (let index = 0; index < value.length; index += 1)
		{
			hash = ((hash << 5) - hash) + value.codePointAt(index);
			hash = Math.trunc(hash);
		}

		return hash;
	}

	loadSavedGroups(): Object
	{
		try
		{
			const raw = localStorage.getItem(STORAGE_KEY);
			const parsed = raw ? JSON.parse(raw) : {};

			return Type.isPlainObject(parsed) ? parsed : {};
		}
		catch
		{
			return {};
		}
	}

	saveGroupsToStorage(): void
	{
		try
		{
			localStorage.setItem(STORAGE_KEY, JSON.stringify(this.savedGroups));
		}
		catch
		{ /* empty */ }
	}

	static instance: ?Abtest = null;

	static getInstance(): Abtest
	{
		if (!Abtest.instance)
		{
			Abtest.instance = new Abtest();
		}

		return Abtest.instance;
	}
}
